import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Activity, 
  Users, 
  Server, 
  TrendingUp,
  Clock,
  CheckCircle,
  Zap,
  Shield,
  Globe,
  Heart
} from 'lucide-react'
import { useSystemStatus } from '@/hooks/useSystemStatus'

interface Metric {
  id: string
  title: string
  value: number
  suffix: string
  icon: React.ComponentType<any>
  color: string
  description: string
  target: number
}

const LiveMetrics = () => {
  const [animatedValues, setAnimatedValues] = useState<Record<string, number>>({})
  const { data: systemStatus } = useSystemStatus()

  // Get real-time data or fallback to defaults
  const getRealTimeMetrics = (): Metric[] => {
    const activeServices = systemStatus?.monitors.filter(m => m.status === 'up').length || 0
    const totalServices = systemStatus?.monitors.length || 16
    const realUptime = systemStatus?.uptimePercentage || 99.96
    const realResponseTime = systemStatus?.avgResponseTime || 45
    const activeIncidents = systemStatus?.totalIncidents24h || 0

    return [
      {
        id: 'active-demos',
        title: 'Active Services',
        value: activeServices,
        suffix: `/${totalServices}`,
        icon: Activity,
        color: activeServices === totalServices ? 'from-green-500 to-emerald-500' : 'from-yellow-500 to-orange-500',
        description: 'Services currently online',
        target: activeServices
      },
      {
        id: 'uptime',
        title: 'Lab Uptime',
        value: realUptime,
        suffix: '%',
        icon: Shield,
        color: realUptime >= 99.5 ? 'from-green-500 to-emerald-500' : realUptime >= 98 ? 'from-yellow-500 to-orange-500' : 'from-red-500 to-pink-500',
        description: 'System availability (24h)',
        target: realUptime
      },
      {
        id: 'response-time',
        title: 'Response Time',
        value: realResponseTime,
        suffix: 'ms',
        icon: Zap,
        color: realResponseTime <= 100 ? 'from-green-500 to-emerald-500' : realResponseTime <= 500 ? 'from-yellow-500 to-orange-500' : 'from-red-500 to-pink-500',
        description: 'Average infrastructure response',
        target: realResponseTime
      },
      {
        id: 'incidents',
        title: 'Active Issues',
        value: activeIncidents,
        suffix: '',
        icon: Heart,
        color: activeIncidents === 0 ? 'from-green-500 to-emerald-500' : activeIncidents <= 2 ? 'from-yellow-500 to-orange-500' : 'from-red-500 to-pink-500',
        description: 'Current infrastructure issues',
        target: activeIncidents
      },
      {
        id: 'sessions',
        title: 'Total Sessions',
        value: 2847,
        suffix: '',
        icon: Users,
        color: 'from-purple-500 to-pink-500',
        description: 'Completed demo sessions',
        target: 2847
      },
      {
        id: 'global-reach',
        title: 'Global Reach',
        value: 34,
        suffix: ' countries',
        icon: Globe,
        color: 'from-indigo-500 to-blue-500',
        description: 'Countries served',
        target: 34
      }
    ]
  }

  const metrics = getRealTimeMetrics()

  // Animate counter values when data changes
  useEffect(() => {
    const animateCounters = () => {
      metrics.forEach((metric) => {
        let currentValue = 0
        const increment = metric.target / 60 // Animate over ~1 second (60 frames)
        
        const timer = setInterval(() => {
          currentValue += increment
          if (currentValue >= metric.target) {
            currentValue = metric.target
            clearInterval(timer)
          }
          
          setAnimatedValues(prev => ({
            ...prev,
            [metric.id]: currentValue
          }))
        }, 16) // ~60fps
      })
    }

    // Reset animated values when system status changes
    setAnimatedValues({})
    
    // Delay animation slightly for better effect
    const timeout = setTimeout(animateCounters, 500)
    return () => clearTimeout(timeout)
  }, [systemStatus]) // Re-animate when system status changes

  const formatValue = (metric: Metric) => {
    const animatedValue = animatedValues[metric.id] || 0
    
    if (metric.id === 'uptime' || metric.id === 'satisfaction') {
      return animatedValue.toFixed(1)
    } else if (metric.id === 'sessions') {
      return Math.floor(animatedValue).toLocaleString()
    } else if (metric.id === 'response-time') {
      return Math.floor(animatedValue)
    } else {
      return Math.floor(animatedValue)
    }
  }

  return (
    <div className="relative">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold mb-4 holographic-text">
            Live Lab Metrics
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Real-time performance indicators showing the pulse of our Demo Lab infrastructure
          </p>
        </motion.div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric, index) => {
            const Icon = metric.icon
            
            return (
              <motion.div
                key={metric.id}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="glass-morphism-dark rounded-xl p-6 hover:shadow-2xl group cursor-pointer relative overflow-hidden"
              >
                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${metric.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-xl`} />
                
                <div className="relative">
                  {/* Icon and Title */}
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${metric.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {metric.title}
                      </h3>
                    </div>
                  </div>

                  {/* Value Display */}
                  <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                      <motion.span
                        className={`text-3xl font-bold text-transparent bg-gradient-to-r ${metric.color} bg-clip-text`}
                        style={{ minWidth: '5ch', display: 'inline-block' }}
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        {formatValue(metric)}
                      </motion.span>
                      <span className={`text-lg font-medium text-transparent bg-gradient-to-r ${metric.color} bg-clip-text`}>
                        {metric.suffix}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {metric.description}
                  </p>

                  {/* Progress Indicator */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                      <motion.div
                        className={`h-1 rounded-full bg-gradient-to-r ${metric.color}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.min((animatedValues[metric.id] || 0) / metric.target * 100, 100)}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.8 + index * 0.1, duration: 1 }}
                      />
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div className="absolute bottom-4 right-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1 + index * 0.1 }}
                      className="w-3 h-3 bg-green-500 rounded-full animate-pulse"
                    />
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Real-time Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 glass-morphism-dark rounded-full border border-green-400/30">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Live Data - Updated {new Date().toLocaleTimeString()}
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LiveMetrics